import React, { useEffect, useMemo, useRef, useState } from "react";
import "./SuperadminEditor.css";
import {
  createDoc,
  deleteDoc,
  DocPayload,
  DocRecord,
  fetchDocs,
  updateDoc,
} from "../../utils/api";
import Modal from "../../components/Modal";

const SUPERADMIN_CLIPBOARD_KEY = "operatorfe-superadmin-clipboard";
const SUPERADMIN_CLIPBOARD_PREFIX = "OPERATORFE_SUPERADMIN_CLIPBOARD=";

type SelectedDocId = string | "new" | null;

const emptyPayload: DocPayload = {
  title: "",
  body: "",
  tags: [],
};

const buildFormState = (doc?: DocRecord | null): DocPayload => ({
  title: doc?.title ?? "",
  body: doc?.body ?? "",
  tags: doc?.tags ?? [],
});

const truncateText = (value?: string | null, limit: number = 96) => {
  const text = (value ?? "").replace(/\s+/g, " ").trim();
  if (!text) {
    return "--";
  }
  return text.length > limit ? `${text.slice(0, limit - 1)}…` : text;
};

const useAutosizeTextarea = (
  ref: React.RefObject<HTMLTextAreaElement | null>,
  value: string
) => {
  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [ref, value]);
};

type PasteDiff = {
  toCreate: DocRecord[];
  toUpdate: { existing: DocRecord; incoming: DocRecord }[];
  toDelete: DocRecord[];
};

const Docs: React.FC = () => {
  const [docs, setDocs] = useState<DocRecord[]>([]);
  const [selectedDocId, setSelectedDocId] = useState<SelectedDocId>(null);
  const [editingDocId, setEditingDocId] = useState<SelectedDocId>(null);
  const [formState, setFormState] = useState<DocPayload>(emptyPayload);
  const [tagInput, setTagInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [pasteDiff, setPasteDiff] = useState<PasteDiff | null>(null);
  const [pasteProcessing, setPasteProcessing] = useState(false);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  useAutosizeTextarea(bodyRef, formState.body);

  const selectedDoc = useMemo(() => {
    if (selectedDocId === "new" || !selectedDocId) {
      return null;
    }
    return docs.find((doc) => doc.docId === selectedDocId) ?? null;
  }, [docs, selectedDocId]);

  const filteredDocs = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return docs;
    }
    return docs.filter((doc) => {
      const haystack = `${doc.title} ${doc.body}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [docs, searchQuery]);

  const isEditing = editingDocId !== null;

  useEffect(() => {
    const loadDocs = async () => {
      try {
        setLoading(true);
        const data = await fetchDocs();
        setDocs(data);
        if (data.length > 0) {
          const firstDoc = data[0];
          setSelectedDocId(firstDoc.docId);
          setFormState(buildFormState(firstDoc));
        }
      } catch (err) {
        console.error("OperatorFE: failed to load docs", err);
        setError("Unable to load docs. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    loadDocs();
  }, []);

  const refreshDocs = async (keepSelection?: string) => {
    const data = await fetchDocs();
    setDocs(data);
    if (data.length === 0) {
      setSelectedDocId(null);
      setFormState(emptyPayload);
      return;
    }
    const nextSelection = keepSelection && data.some((doc) => doc.docId === keepSelection)
      ? keepSelection
      : data[0].docId;
    setSelectedDocId(nextSelection);
    const activeDoc = data.find((doc) => doc.docId === nextSelection);
    setFormState(buildFormState(activeDoc));
  };

  const handleSelectDoc = (docId: string) => {
    if (isEditing && editingDocId !== docId) {
      return;
    }
    setError(null);
    setSelectedDocId(docId);
    const doc = docs.find((item) => item.docId === docId);
    setFormState(buildFormState(doc));
    setEditingDocId(null);
    setTagInput("");
  };

  const handleStartEditing = () => {
    if (!selectedDocId) {
      return;
    }
    setError(null);
    setEditingDocId(selectedDocId);
    if (selectedDocId === "new") {
      setFormState(emptyPayload);
    } else if (selectedDoc) {
      setFormState(buildFormState(selectedDoc));
    }
  };

  const handleNewDoc = () => {
    setError(null);
    setSelectedDocId("new");
    setEditingDocId("new");
    setFormState(emptyPayload);
    setTagInput("");
  };

  const handleFormChange = (field: keyof DocPayload, value: string) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddTag = () => {
    const trimmed = tagInput.trim();
    if (!trimmed) {
      return;
    }
    setFormState((prev) => ({
      ...prev,
      tags: Array.from(new Set([...(prev.tags ?? []), trimmed])),
    }));
    setTagInput("");
  };

  const handleRemoveTag = (tag: string) => {
    setFormState((prev) => ({
      ...prev,
      tags: (prev.tags ?? []).filter((item) => item !== tag),
    }));
  };

  const handleTagKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      handleAddTag();
    }
  };

  const handleCancelEdit = () => {
    setError(null);
    if (selectedDocId === "new") {
      if (docs[0]) {
        setSelectedDocId(docs[0].docId);
        setFormState(buildFormState(docs[0]));
      } else {
        setSelectedDocId(null);
        setFormState(emptyPayload);
      }
    } else if (selectedDoc) {
      setFormState(buildFormState(selectedDoc));
    }
    setEditingDocId(null);
    setTagInput("");
  };

  const handleSave = async () => {
    if (!formState.title.trim()) {
      setError("Title is required.");
      return;
    }
    if (!formState.body.trim()) {
      setError("Body is required.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (selectedDocId === "new" || !selectedDocId) {
        const created = await createDoc({
          ...formState,
          title: formState.title.trim(),
          body: formState.body.trim(),
        });
        await refreshDocs(created.docId);
      } else {
        const updated = await updateDoc(selectedDocId, {
          ...formState,
          title: formState.title.trim(),
          body: formState.body.trim(),
        });
        await refreshDocs(updated.docId);
      }
      setEditingDocId(null);
    } catch (err) {
      console.error("OperatorFE: failed to save doc", err);
      setError("Unable to save the doc. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const promptDelete = () => {
    if (!selectedDocId || selectedDocId === "new") {
      return;
    }
    setConfirmDeleteId(selectedDocId);
  };

  const handleConfirmDelete = async () => {
    if (!confirmDeleteId) {
      return;
    }
    setSaving(true);
    try {
      await deleteDoc(confirmDeleteId);
      setConfirmDeleteId(null);
      await refreshDocs();
      setEditingDocId(null);
    } catch (err) {
      console.error("OperatorFE: failed to delete doc", err);
      setError("Unable to delete the doc. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (value?: string) => {
    if (!value) {
      return "--";
    }
    try {
      return new Date(value).toLocaleString();
    } catch {
      return value;
    }
  };

  const copyAllDocs = async () => {
    if (docs.length === 0) {
      return;
    }
    const clipboardPayload = {
      type: "docs-bulk",
      data: docs,
      ts: Date.now(),
    };
    try {
      const serialized = JSON.stringify(clipboardPayload);
      localStorage.setItem(SUPERADMIN_CLIPBOARD_KEY, serialized);
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(`${SUPERADMIN_CLIPBOARD_PREFIX}${serialized}`);
      }
    } catch (clipError) {
      console.error("OperatorFE: failed to copy docs", clipError);
    }
  };

  const readBulkDocsClipboard = async (): Promise<DocRecord[] | null> => {
    if (typeof window === "undefined") {
      return null;
    }
    if (navigator?.clipboard?.readText) {
      try {
        const text = await navigator.clipboard.readText();
        if (text?.startsWith(SUPERADMIN_CLIPBOARD_PREFIX)) {
          const payload = JSON.parse(text.slice(SUPERADMIN_CLIPBOARD_PREFIX.length));
          if (payload?.type === "docs-bulk" && Array.isArray(payload?.data)) {
            return payload.data as DocRecord[];
          }
        }
      } catch (clipError) {
        console.error("OperatorFE: failed to read clipboard (docs-bulk)", clipError);
      }
    }
    try {
      const raw = localStorage.getItem(SUPERADMIN_CLIPBOARD_KEY);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw);
      if (parsed?.type === "docs-bulk" && Array.isArray(parsed?.data)) {
        return parsed.data as DocRecord[];
      }
    } catch (clipError) {
      console.error("OperatorFE: failed to read docs-bulk clipboard", clipError);
    }
    return null;
  };

  const docsAreEqual = (a: DocRecord, b: DocRecord): boolean => {
    return (
      a.title === b.title &&
      a.body === b.body &&
      JSON.stringify(a.tags ?? []) === JSON.stringify(b.tags ?? [])
    );
  };

  const computePasteDiff = (incoming: DocRecord[]): PasteDiff => {
    const existingByTitle = new Map(docs.map((doc) => [doc.title, doc]));
    const incomingByTitle = new Map(incoming.map((doc) => [doc.title, doc]));

    const toCreate: DocRecord[] = [];
    const toUpdate: { existing: DocRecord; incoming: DocRecord }[] = [];
    const toDelete: DocRecord[] = [];

    for (const doc of incoming) {
      const existing = existingByTitle.get(doc.title);
      if (existing) {
        if (!docsAreEqual(existing, doc)) {
          toUpdate.push({ existing, incoming: doc });
        }
      } else {
        toCreate.push(doc);
      }
    }

    for (const doc of docs) {
      if (!incomingByTitle.has(doc.title)) {
        toDelete.push(doc);
      }
    }

    return { toCreate, toUpdate, toDelete };
  };

  const handlePasteAllClick = async () => {
    const incoming = await readBulkDocsClipboard();
    if (!incoming || incoming.length === 0) {
      setError("Clipboard does not contain doc data.");
      return;
    }
    setError(null);
    const diff = computePasteDiff(incoming);
    setPasteDiff(diff);
  };

  const handleConfirmPaste = async () => {
    if (!pasteDiff) {
      return;
    }
    setPasteProcessing(true);
    setError(null);
    try {
      // Delete docs not in incoming
      for (const doc of pasteDiff.toDelete) {
        await deleteDoc(doc.docId);
      }
      // Update existing docs
      for (const { existing, incoming } of pasteDiff.toUpdate) {
        await updateDoc(existing.docId, {
          title: incoming.title,
          body: incoming.body,
          tags: incoming.tags ?? [],
        });
      }
      // Create new docs
      for (const doc of pasteDiff.toCreate) {
        await createDoc({
          title: doc.title,
          body: doc.body,
          tags: doc.tags ?? [],
        });
      }
      await refreshDocs();
      setPasteDiff(null);
    } catch (err) {
      console.error("OperatorFE: failed to paste docs", err);
      setError("Failed to apply paste. Please try again.");
    } finally {
      setPasteProcessing(false);
    }
  };

  const renderDetail = () => {
    if (loading) {
      return <div className="docs-empty">Loading docs...</div>;
    }
    if (!selectedDocId) {
      return <div className="docs-empty">Select or create a doc to begin.</div>;
    }
    return (
      <div className="docs-editor">
        <div className="docs-editor-actions">
          <div className="docs-editor-actions-left">
            {isEditing ? (
              <>
                <button
                  type="button"
                  className="docs-button docs-button--primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="docs-button docs-button--secondary"
                  onClick={handleCancelEdit}
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="docs-button docs-button--secondary"
                  onClick={handleStartEditing}
                  disabled={!selectedDocId}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="button-danger"
                  onClick={promptDelete}
                  disabled={!selectedDocId || selectedDocId === "new"}
                >
                  Delete
                </button>
              </>
            )}
          </div>
        </div>
        {error && <div className="docs-error">{error}</div>}
        <div className="docs-editor-body">
          <div className="docs-field">
            <label htmlFor="doc-title">Title</label>
            <input
              id="doc-title"
              type="text"
              value={formState.title}
              onChange={(event) => handleFormChange("title", event.target.value)}
              placeholder="Parallel Pay workflows"
              disabled={!isEditing}
            />
          </div>
          <div className="docs-field">
            <label htmlFor="doc-body">Body</label>
            <textarea
              id="doc-body"
              ref={bodyRef}
              rows={14}
              value={formState.body}
              onChange={(event) => handleFormChange("body", event.target.value)}
              placeholder="Markdown or rich text instructions..."
              disabled={!isEditing}
            />
          </div>
          <div className="docs-field">
            <label>Tags</label>
            <div className="docs-tags">
              {isEditing && (
                <div className="docs-tag-input">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(event) => setTagInput(event.target.value)}
                    onKeyDown={handleTagKeyDown}
                    placeholder="Add tag and press Enter"
                  />
                  <button
                    type="button"
                    className="docs-button docs-button--primary"
                    onClick={handleAddTag}
                    disabled={!tagInput.trim()}
                  >
                    Add
                  </button>
                </div>
              )}
              <div className="docs-tag-list">
                {formState.tags.length === 0 && !isEditing && <span>No tags added</span>}
                {formState.tags.map((tag) => (
                  <span key={tag} className="docs-tag">
                    {tag}
                    {isEditing && (
                      <button type="button" onClick={() => handleRemoveTag(tag)} aria-label={`Remove ${tag}`}>
                        ×
                      </button>
                    )}
                  </span>
                ))}
              </div>
            </div>
          </div>
          {selectedDoc && (
            <div className="docs-info">
              <span>Created: {formatDate(selectedDoc.createdOn)}</span>
              <span>Updated: {formatDate(selectedDoc.modifiedOn)}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="docs-page">
      <div className="docs-shell">
        <div className="docs-header">
          <div>
            <h1>Knowledge Docs</h1>
            <p>Curate internal documentation that gives every operator clear workflows and guardrails.</p>
          </div>
          <div className="docs-actions">
            <button
              type="button"
              className="docs-button docs-button--primary"
              onClick={handleNewDoc}
              disabled={isEditing && editingDocId !== "new"}
            >
              New Doc
            </button>
          </div>
        </div>

        <div className="docs-grid">
          <aside className="docs-pane">
            <div className="docs-pane-header">
              <h2>Docs</h2>
              <div className="docs-bulk-actions">
                <button
                  type="button"
                  className="docs-button docs-button--secondary"
                  onClick={copyAllDocs}
                  disabled={docs.length === 0 || isEditing}
                >
                  Copy All
                </button>
                <button
                  type="button"
                  className="docs-button docs-button--secondary"
                  onClick={handlePasteAllClick}
                  disabled={isEditing}
                >
                  Paste All
                </button>
              </div>
              <input
                type="text"
                className="docs-search-input"
                placeholder="Search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
            </div>
            <div className="docs-pane-body">
              {filteredDocs.length === 0 && !loading ? (
                <div className="docs-empty">No docs match your search.</div>
              ) : (
                <ul className="docs-list">
                  {filteredDocs.map((doc) => (
                    <li
                      key={doc.docId}
                      className={`docs-list-item ${selectedDocId === doc.docId ? "active" : ""}`}
                      onClick={() => handleSelectDoc(doc.docId)}
                    >
                      <div className="docs-list-title">{doc.title}</div>
                      <div className="docs-list-meta">
                        <span>{formatDate(doc.modifiedOn)}</span>
                        <p className="docs-list-description">{truncateText(doc.body)}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </aside>
          <section className="docs-pane">{renderDetail()}</section>
        </div>
      </div>

      <Modal
        isOpen={Boolean(confirmDeleteId)}
        title="Delete doc?"
        description="This action cannot be undone. The document will be removed from the AI knowledge base."
        confirmText="Delete"
        cancelText="Cancel"
        onCancel={() => setConfirmDeleteId(null)}
        onConfirm={handleConfirmDelete}
        disableConfirm={saving}
      />

      <Modal
        isOpen={Boolean(pasteDiff)}
        title="Confirm Paste All"
        confirmText="Confirm"
        cancelText="Cancel"
        onCancel={() => setPasteDiff(null)}
        onConfirm={handleConfirmPaste}
        disableConfirm={pasteProcessing}
        customContent={
          pasteDiff && (
            <div className="docs-paste-diff">
              {pasteDiff.toCreate.length > 0 && (
                <div className="docs-paste-section">
                  <strong>Creating ({pasteDiff.toCreate.length}):</strong>
                  <ul>
                    {pasteDiff.toCreate.map((doc) => (
                      <li key={doc.title}>{doc.title}</li>
                    ))}
                  </ul>
                </div>
              )}
              {pasteDiff.toUpdate.length > 0 && (
                <div className="docs-paste-section">
                  <strong>Updating ({pasteDiff.toUpdate.length}):</strong>
                  <ul>
                    {pasteDiff.toUpdate.map(({ existing }) => (
                      <li key={existing.title}>{existing.title}</li>
                    ))}
                  </ul>
                </div>
              )}
              {pasteDiff.toDelete.length > 0 && (
                <div className="docs-paste-section">
                  <strong>Deleting ({pasteDiff.toDelete.length}):</strong>
                  <ul>
                    {pasteDiff.toDelete.map((doc) => (
                      <li key={doc.title}>{doc.title}</li>
                    ))}
                  </ul>
                </div>
              )}
              {pasteDiff.toCreate.length === 0 && pasteDiff.toUpdate.length === 0 && pasteDiff.toDelete.length === 0 && (
                <p>No changes to apply.</p>
              )}
            </div>
          )
        }
      />
    </div>
  );
};

export default Docs;
